import { useEffect, useState } from 'react'
import { useForm, useFieldArray } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { IconLoader2, IconPlus, IconTrash, IconLanguage, IconCircleCheck, IconUserPlus, IconMail, IconPhone, IconClock, IconPencil, IconUsers, IconBriefcase } from '@tabler/icons-react'
import { Header } from '@/components/layout/header'
import { Main } from '@/components/layout/main'
import { TopNav } from '@/components/layout/top-nav'
import { ProfileDropdown } from '@/components/profile-dropdown'
import { Search as SearchComponent } from '@/components/search'
import { ThemeSwitch } from '@/components/theme-switch'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Progress } from '@/components/ui/progress'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useTeamPage } from '@/services/use-team-page'
import { toast } from 'sonner'
import { MediaPicker } from '@/components/media/media-picker'
import { getMediaUrl } from '@/utils/url-utils'

const localizedStringSchema = z.object({
  tr: z.string().min(1, 'Türkçe alan zorunludur'),
  en: z.string().optional(),
  fr: z.string().optional(),
  es: z.string().optional(),
  ru: z.string().optional(),
  nl: z.string().optional(),
  ar: z.string().optional(),
})

const teamSchema = z.object({
  hero: z.object({
    title: localizedStringSchema,
    description: localizedStringSchema,
  }),
  stats: z.array(z.object({
    value: z.string().min(1, 'Değer zorunludur'),
    label: localizedStringSchema,
  })),
  members: z.array(z.object({
    name: z.string().min(1, 'İsim zorunludur'),
    role: localizedStringSchema,
    category: localizedStringSchema,
    experience: localizedStringSchema.optional(),
    email: z.string().email('Geçersiz e-posta').optional().or(z.literal('')),
    phone: z.string().optional(),
    imageUrl: z.string().optional(),
  })),
  bottomContent: z.object({
    title: localizedStringSchema,
    description: localizedStringSchema,
  }).optional(),
})

type TeamFormValues = z.infer<typeof teamSchema>

const topNav = [
  { title: 'Anasayfa', href: '/', isActive: false },
  { title: 'Ekip CMS', href: '/ekip', isActive: true },
]

const LANGUAGES = [
  { code: 'tr', label: 'Türkçe' },
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'Français' },
  { code: 'es', label: 'Español' },
  { code: 'ru', label: 'Русский' },
  { code: 'nl', label: 'Nederlands' },
  { code: 'ar', label: 'العربية' },
]

export default function Ekip() {
  const { teamPage, isLoading, updateTeamPage, isUpdating } = useTeamPage()
  const [isNormalSaving, setIsNormalSaving] = useState(false)
  const [isAISaving, setIsAISaving] = useState(false)
  const [showProgress, setShowProgress] = useState(false)
  const [progress, setProgress] = useState(0)
  const [currentStep, setCurrentStep] = useState('')
  const [memberSearchQuery, setMemberSearchQuery] = useState('')
  const [isMemberDialogOpen, setIsMemberDialogOpen] = useState(false)
  const [editingMemberIndex, setEditingMemberIndex] = useState<number | null>(null)
  const [isPickerOpen, setIsPickerOpen] = useState(false)
  const [pickerIndex, setPickerIndex] = useState<number | null>(null)

  const form = useForm<TeamFormValues>({
    resolver: zodResolver(teamSchema),
    defaultValues: {
      hero: { title: { tr: '' }, description: { tr: '' } },
      stats: [],
      members: [],
      bottomContent: { title: { tr: '' }, description: { tr: '' } }
    },
  })

  const { fields: statFields, append: appendStat, remove: removeStat } = useFieldArray({
    control: form.control,
    name: 'stats',
  })

  const { fields: memberFields, append: appendMember, remove: removeMember } = useFieldArray({
    control: form.control,
    name: 'members',
  })


  useEffect(() => {
    if (teamPage) {
      form.reset({
        hero: teamPage.hero,
        stats: teamPage.stats,
        members: teamPage.members,
        bottomContent: teamPage.bottomContent || { title: { tr: '' }, description: { tr: '' } }
      })
    }
  }, [teamPage, form])

  const onSubmit = async (values: TeamFormValues) => {
    setIsNormalSaving(true)
    try {
      await updateTeamPage({ data: values })
    } finally {
      setIsNormalSaving(false)
    }
  }

  const onTranslateAndSave = async () => {
    const values = form.getValues()
    setIsAISaving(true)
    setShowProgress(true)
    setProgress(5)
    setCurrentStep('AI Çeviri servisine bağlanılıyor...')

    const steps = [
      { p: 20, m: 'Hero bölümü çeviriliyor...' },
      { p: 40, m: 'İstatistikler işleniyor...' },
      { p: 70, m: 'Ekip üyelerinin pozisyonları ve bilgileri çeviriliyor...' },
      { p: 90, m: 'Alt içerik metinleri düzenleniyor...' },
      { p: 98, m: 'Veriler kaydediliyor...' },
    ]

    let currentStepIdx = 0
    const interval = setInterval(() => {
      if (currentStepIdx < steps.length) {
        setProgress(steps[currentStepIdx].p)
        setCurrentStep(steps[currentStepIdx].m)
        currentStepIdx++
      }
    }, 1500)

    try {
      await updateTeamPage({ data: values, translate: true })
      clearInterval(interval)
      setProgress(100)
      setCurrentStep('Tüm diller başarıyla kaydedildi!')
      setTimeout(() => setShowProgress(false), 2000)
    } catch (error) {
      clearInterval(interval)
      setShowProgress(false)
    } finally {
      setIsAISaving(false)
    }
  }

  if (isLoading) {
    return (
      <>
        <Header>
          <TopNav links={topNav} />
          <div className="ml-auto flex items-center gap-2">
            <SearchComponent />
            <ThemeSwitch />
            <ProfileDropdown />
          </div>
        </Header>
        <Main>
          <div className="flex h-[80vh] items-center justify-center">
            <IconLoader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </Main>
      </>
    )
  }

  return (
    <>
      <Header>
        <TopNav links={topNav} />
        <div className="ml-auto flex items-center gap-2">
          <SearchComponent />
          <ThemeSwitch />
          <ProfileDropdown />
        </div>
      </Header>

      <Main>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            <div className='flex flex-wrap items-center justify-between space-y-2'>
              <div>
                <h2 className='text-2xl font-bold tracking-tight'>Ekip Yönetimi</h2>
                <p className='text-muted-foreground'>
                  Kurumsal ekibinizi ve departmanları yönetin.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onTranslateAndSave}
                  disabled={isUpdating}
                  className="space-x-1 border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 hover:text-blue-800"
                >
                  <span>AI ile Çevir ve Kaydet</span>
                  {isAISaving ? <IconLoader2 size={18} className="animate-spin" /> : <IconLanguage size={18} />}
                </Button>
                <Button type="submit" disabled={isUpdating} className="space-x-1">
                  <span>Normal Kaydet</span>
                  {isNormalSaving ? <IconLoader2 size={18} className="animate-spin" /> : <IconCircleCheck size={18} />}
                </Button>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Toplam Personel</CardTitle>
                  <IconUsers className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-primary">{memberFields.length}</div>
                  <p className="text-xs text-muted-foreground">Şirket genelindeki aktif ekip üyeleri</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Departmanlar</CardTitle>
                  <IconBriefcase className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-primary">
                    {new Set(memberFields.map((_, i) => form.getValues(`members.${i}.category.tr`))).size}
                  </div>
                  <p className="text-xs text-muted-foreground">Tanımlı organizasyonel birimler</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">İletişim Bilgileri</CardTitle>
                  <IconMail className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-primary">
                    {memberFields.filter((_, i) => form.getValues(`members.${i}.email`) || form.getValues(`members.${i}.phone`)).length}
                  </div>
                  <p className="text-xs text-muted-foreground">E-posta veya telefon verisi girilmiş üyeler</p>
                </CardContent>
              </Card>
            </div>

            <Tabs defaultValue="hero" className="w-full">
              <TabsList className="grid w-full grid-cols-4 h-auto">
                <TabsTrigger value="hero" className="py-2">Giriş (Hero)</TabsTrigger>
                <TabsTrigger value="stats" className="py-2">İstatistikler</TabsTrigger>
                <TabsTrigger value="members" className="py-2">Ekip Üyeleri</TabsTrigger>
                <TabsTrigger value="bottom" className="py-2">Alt İçerik</TabsTrigger>
              </TabsList>

              {/* HERO SECTION */}
              <TabsContent value="hero" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Sayfa Başlığı ve Açıklama</CardTitle>
                    <CardDescription>Ekip sayfasının en üstündeki karşılama metinleri.</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <Tabs defaultValue="tr" className="w-full">
                      <TabsList className="w-full justify-start overflow-x-auto h-auto p-1 bg-muted/50">
                        {LANGUAGES.map(lang => (
                          <TabsTrigger key={lang.code} value={lang.code} className="text-xs px-2 py-1">{lang.label}</TabsTrigger>
                        ))}
                      </TabsList>
                      {LANGUAGES.map(lang => (
                        <TabsContent key={lang.code} value={lang.code} className="space-y-4 pt-4">
                          <FormField
                            control={form.control}
                            name={`hero.title.${lang.code}` as any}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Başlık ({lang.label})</FormLabel>
                                <FormControl>
                                  <Input {...field} placeholder="Örn: Güçlü Bir Ekiple Büyüyoruz" />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name={`hero.description.${lang.code}` as any}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Açıklama ({lang.label})</FormLabel>
                                <FormControl>
                                  <Textarea {...field} placeholder="Ekip sayfası açıklaması..." rows={3} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </TabsContent>
                      ))}
                    </Tabs>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* STATS SECTION */}
              <TabsContent value="stats" className="space-y-4 pt-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-medium">Sayılarla Ekibimiz</h3>
                  <Button type="button" variant="outline" size="sm" onClick={() => appendStat({ value: '0', label: { tr: '' } })}>
                    <IconPlus className="mr-2 h-4 w-4" /> Kart Ekle
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {statFields.map((field, index) => (
                    <Card key={field.id}>
                      <CardContent className="pt-6 space-y-4">
                        <div className="flex justify-end">
                          <Button type="button" variant="ghost" size="icon" className="h-6 w-6 text-destructive" onClick={() => removeStat(index)}>
                            <IconTrash className="h-4 w-4" />
                          </Button>
                        </div>
                        <FormField
                          control={form.control}
                          name={`stats.${index}.value`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-xs">Değer (Örn: 100+)</FormLabel>
                              <FormControl>
                                <Input {...field} className="font-bold underline" />
                              </FormControl>
                            </FormItem>
                          )}
                        />
                        <Tabs defaultValue="tr" className="w-full">
                          <TabsList className="w-full justify-start h-auto p-0 bg-transparent gap-1 overflow-x-auto">
                            {LANGUAGES.map(lang => (
                              <TabsTrigger key={lang.code} value={lang.code} className="text-[10px] px-1 py-0.5">{lang.code.toUpperCase()}</TabsTrigger>
                            ))}
                          </TabsList>
                          {LANGUAGES.map(lang => (
                            <TabsContent key={lang.code} value={lang.code}>
                              <FormField
                                control={form.control}
                                name={`stats.${index}.label.${lang.code}` as any}
                                render={({ field }) => (
                                  <FormItem>
                                    <FormControl>
                                      <Input {...field} placeholder="Etiket" className="h-7 text-xs" />
                                    </FormControl>
                                  </FormItem>
                                )}
                              />
                            </TabsContent>
                          ))}
                        </Tabs>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </TabsContent>

              {/* MEMBERS SECTION */}
              <TabsContent value="members" className="space-y-6 pt-4">
                <div className='flex items-center justify-between py-1'>
                  <div className='flex flex-1 flex-col-reverse items-start gap-y-2 sm:flex-row sm:items-center sm:space-x-2'>
                    <Input
                      placeholder='İsim, Pozisyon veya Departman ile Ara'
                      value={memberSearchQuery}
                      onChange={(e) => setMemberSearchQuery(e.target.value)}
                      className='h-8 w-[150px] lg:w-[250px]'
                    />
                  </div>
                  <div className='flex gap-2'>
                    <Button
                      type='button'
                      variant='outline'
                      className='space-x-1 h-8 px-2 lg:px-3 text-xs'
                      onClick={() => {
                        appendMember({
                          name: '',
                          role: { tr: '' },
                          category: { tr: 'Yönetim' },
                          experience: { tr: '' },
                          email: '',
                          phone: '',
                          imageUrl: ''
                        })
                        setEditingMemberIndex(memberFields.length)
                        setIsMemberDialogOpen(true)
                      }}
                    >
                      <span>Yeni Üye</span> <IconUserPlus size={16} />
                    </Button>
                  </div>
                </div>

                <div className='-mx-4 flex-1 overflow-auto px-4 py-1 lg:flex-row lg:space-x-12 lg:space-y-0'>
                  <div className='rounded-md border'>
                    <Table>
                      <TableHeader>
                        <TableRow className='group/row'>
                          <TableHead className="w-[80px]">Profil</TableHead>
                          <TableHead>Üye Bilgileri</TableHead>
                          <TableHead>Departman & Pozisyon</TableHead>
                          <TableHead className="hidden md:table-cell">İletişim</TableHead>
                          <TableHead className="text-right">İşlemler</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {memberFields.map((field, index) => {
                          const member = form.watch(`members.${index}`) || {}
                          const name = member.name || ''
                          const role = member.role?.tr || ''
                          const category = member.category?.tr || ''
                          const email = member.email || ''
                          const phone = member.phone || ''
                          const imageUrl = member.imageUrl || ''

                          const query = memberSearchQuery.toLowerCase()
                          if (query && !name.toLowerCase().includes(query) && !role.toLowerCase().includes(query) && !category.toLowerCase().includes(query)) {
                            return null
                          }

                          return (
                            <TableRow key={field.id} className="group hover:bg-muted/40 transition-colors">
                              <TableCell>
                                <Avatar className="h-9 w-9 ring-1 ring-muted shadow-sm">
                                  <AvatarImage src={getMediaUrl(imageUrl)} />
                                  <AvatarFallback className="bg-primary/5 text-primary text-[10px] font-bold">
                                    {name ? name[0] : '?'}
                                  </AvatarFallback>
                                </Avatar>
                              </TableCell>
                              <TableCell>
                                <div className="flex flex-col">
                                  <span className="font-semibold text-sm text-foreground">{name || 'İsimsiz'}</span>
                                  <span className="text-[10px] text-muted-foreground md:hidden">{role}</span>
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex flex-col gap-0.5">
                                  <Badge variant="outline" className="w-fit text-[10px] px-1.5 py-0 font-normal bg-muted/30">
                                    {category}
                                  </Badge>
                                  <span className="text-[11px] font-medium text-muted-foreground">{role}</span>
                                </div>
                              </TableCell>
                              <TableCell className="hidden md:table-cell py-2">
                                <div className="flex flex-col gap-0.5 text-[10px] text-muted-foreground font-medium">
                                  {email && <span className="flex items-center gap-1"><IconMail size={12} className="opacity-70" /> {email}</span>}
                                  {phone && <span className="flex items-center gap-1"><IconPhone size={12} className="opacity-70" /> {phone}</span>}
                                  {!email && !phone && <span className="italic opacity-30 text-[9px]">Veri yok</span>}
                                </div>
                              </TableCell>
                              <TableCell className="text-right py-2">
                                <div className="flex justify-end gap-1">
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                    onClick={() => {
                                      setEditingMemberIndex(index)
                                      setIsMemberDialogOpen(true)
                                    }}
                                  >
                                    <IconPencil size={14} />
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 text-destructive hover:bg-destructive/10"
                                    onClick={() => removeMember(index)}
                                  >
                                    <IconTrash size={14} />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          )
                        })}
                        {memberFields.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={5} className="h-48 text-center bg-muted/5">
                              <div className="flex flex-col items-center justify-center gap-2">
                                <IconUsers className="h-8 w-8 text-muted-foreground/30" />
                                <p className="text-sm text-muted-foreground font-medium italic">Henüz ekip üyesi tanımlanmamış.</p>
                                <Button type="button" variant="outline" size="sm" className="mt-2 space-x-1" onClick={() => {
                                  appendMember({
                                    name: '',
                                    role: { tr: '' },
                                    category: { tr: 'Yönetim' },
                                    experience: { tr: '' },
                                    email: '',
                                    phone: '',
                                    imageUrl: ''
                                  })
                                  setEditingMemberIndex(memberFields.length)
                                  setIsMemberDialogOpen(true)
                                }}>
                                  <span>İlk Üyeyi Ekle</span> <IconUserPlus size={16} />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                {/* MEMBER DIALOG */}
                <Dialog open={isMemberDialogOpen} onOpenChange={setIsMemberDialogOpen}>
                  <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        {editingMemberIndex !== null ? <IconPencil className="h-5 w-5" /> : <IconUserPlus className="h-5 w-5" />}
                        {editingMemberIndex !== null ? 'Üye Bilgilerini Güncelle' : 'Yeni Ekip Üyesi Ekle'}
                      </DialogTitle>
                      <DialogDescription>
                        Ekip üyesinin tüm dillerdeki bilgilerini ve iletişim detaylarını buradan yönetebilirsiniz.
                      </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-6 py-4">
                      {/* Form Fields for Member */}
                      {editingMemberIndex !== null && (() => {
                        const index = editingMemberIndex;
                        return (
                          <div className="space-y-6">
                            <div className="flex flex-col md:flex-row gap-6">
                              <div className="flex flex-col items-center gap-4">
                                <div className="relative group/avatar cursor-pointer" onClick={() => { setPickerIndex(index); setIsPickerOpen(true); }}>
                                  <Avatar className="h-32 w-32 ring-4 ring-muted shadow-xl transition-transform group-hover/avatar:scale-105">
                                    <AvatarImage src={getMediaUrl(form.watch(`members.${index}.imageUrl`))} />
                                    <AvatarFallback className="bg-primary/10 text-primary text-2xl">
                                      {form.watch(`members.${index}.name`) ? form.watch(`members.${index}.name`)[0] : '?'}
                                    </AvatarFallback>
                                  </Avatar>
                                  <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full opacity-0 group-hover/avatar:opacity-100 transition-opacity">
                                    <IconPlus className="h-10 w-10 text-white" />
                                  </div>
                                </div>
                                <Button type="button" variant="outline" size="sm" onClick={() => { setPickerIndex(index); setIsPickerOpen(true); }}>
                                  Kütüphaneden Seç
                                </Button>
                              </div>

                              <div className="flex-1 space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <FormField
                                    control={form.control}
                                    name={`members.${index}.name`}
                                    render={({ field }) => (
                                      <FormItem>
                                        <FormLabel>İsim Soyisim</FormLabel>
                                        <FormControl>
                                          <Input {...field} placeholder="Ad Soyad" />
                                        </FormControl>
                                        <FormMessage />
                                      </FormItem>
                                    )}
                                  />
                                  <FormField
                                    control={form.control}
                                    name={`members.${index}.imageUrl`}
                                    render={({ field }) => (
                                      <FormItem>
                                        <FormLabel>Fotoğraf URL</FormLabel>
                                        <FormControl>
                                          <Input {...field} readOnly className="bg-muted" />
                                        </FormControl>
                                      </FormItem>
                                    )}
                                  />
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <FormField
                                    control={form.control}
                                    name={`members.${index}.email`}
                                    render={({ field }) => (
                                      <FormItem>
                                        <FormLabel className="flex items-center gap-1.5 font-medium"><IconMail className="h-3 w-3" /> E-posta</FormLabel>
                                        <FormControl>
                                          <Input {...field} placeholder="email@skgmetal.com" />
                                        </FormControl>
                                      </FormItem>
                                    )}
                                  />
                                  <FormField
                                    control={form.control}
                                    name={`members.${index}.phone`}
                                    render={({ field }) => (
                                      <FormItem>
                                        <FormLabel className="flex items-center gap-1.5 font-medium"><IconPhone className="h-3 w-3" /> Telefon</FormLabel>
                                        <FormControl>
                                          <Input {...field} placeholder="+90 216 ..." />
                                        </FormControl>
                                      </FormItem>
                                    )}
                                  />
                                </div>
                              </div>
                            </div>

                            <Tabs defaultValue="tr" className="w-full">
                              <TabsList className="w-full justify-start overflow-x-auto h-auto p-1 bg-muted/50">
                                {LANGUAGES.map(lang => (
                                  <TabsTrigger key={lang.code} value={lang.code} className="text-xs px-4 py-1.5">{lang.label}</TabsTrigger>
                                ))}
                              </TabsList>
                              {LANGUAGES.map(lang => (
                                <TabsContent key={lang.code} value={lang.code} className="space-y-4 pt-4 animate-in fade-in-50 duration-300">
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <FormField
                                      control={form.control}
                                      name={`members.${index}.category.${lang.code}` as any}
                                      render={({ field }) => (
                                        <FormItem>
                                          <FormLabel className="font-semibold">Departman ({lang.code.toUpperCase()})</FormLabel>
                                          <FormControl>
                                            <Input {...field} />
                                          </FormControl>
                                        </FormItem>
                                      )}
                                    />
                                    <FormField
                                      control={form.control}
                                      name={`members.${index}.role.${lang.code}` as any}
                                      render={({ field }) => (
                                        <FormItem>
                                          <FormLabel className="font-semibold">Pozisyon ({lang.code.toUpperCase()})</FormLabel>
                                          <FormControl>
                                            <Input {...field} />
                                          </FormControl>
                                        </FormItem>
                                      )}
                                    />
                                  </div>
                                  <FormField
                                    control={form.control}
                                    name={`members.${index}.experience.${lang.code}` as any}
                                    render={({ field }) => (
                                      <FormItem>
                                        <FormLabel className="flex items-center gap-1 font-semibold"><IconClock className="h-3 w-3" /> Deneyim ({lang.code.toUpperCase()})</FormLabel>
                                        <FormControl>
                                          <Input {...field} placeholder="Örn: 15+ Yıl" />
                                        </FormControl>
                                      </FormItem>
                                    )}
                                  />
                                </TabsContent>
                              ))}
                            </Tabs>
                          </div>
                        );
                      })()}
                    </div>

                    <div className="flex justify-end pt-4 border-t gap-2">
                      <Button type="button" variant="outline" onClick={() => setIsMemberDialogOpen(false)}>
                        İptal / Kapat
                      </Button>
                      <Button
                        type="button"
                        onClick={async () => {
                          setIsMemberDialogOpen(false)
                          await onSubmit(form.getValues())
                          toast.success('Değişiklikler kaydedildi.')
                        }}
                        disabled={isNormalSaving || isAISaving}
                      >
                        {isNormalSaving || isAISaving ? <IconLoader2 className="mr-2 h-4 w-4 animate-spin" /> : <IconCircleCheck className="mr-2 h-4 w-4" />}
                        Tamamlandı ve Kaydet
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </TabsContent>

              {/* BOTTOM CONTENT SECTION */}
              <TabsContent value="bottom" className="space-y-4 pt-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Alt Metin Alanı</CardTitle>
                    <CardDescription>Sayfanın en altındaki kurumsal mesaj veya slogan bölümü.</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <Tabs defaultValue="tr" className="w-full">
                      <TabsList className="w-full justify-start overflow-x-auto h-auto p-1 bg-muted/50">
                        {LANGUAGES.map(lang => (
                          <TabsTrigger key={lang.code} value={lang.code} className="text-xs px-2 py-1">{lang.label}</TabsTrigger>
                        ))}
                      </TabsList>
                      {LANGUAGES.map(lang => (
                        <TabsContent key={lang.code} value={lang.code} className="space-y-4 pt-4">
                          <FormField
                            control={form.control}
                            name={`bottomContent.title.${lang.code}` as any}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Alt Başlık ({lang.label})</FormLabel>
                                <FormControl>
                                  <Input {...field} placeholder="Örn: Üretimin Kalbindeyiz" />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name={`bottomContent.description.${lang.code}` as any}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Alt Açıklama ({lang.label})</FormLabel>
                                <FormControl>
                                  <Textarea {...field} placeholder="Kurumsal mesajınız..." rows={4} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </TabsContent>
                      ))}
                    </Tabs>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </form>
        </Form>
      </Main>

      <Dialog open={showProgress} onOpenChange={setShowProgress}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <IconLanguage className="h-5 w-5 text-blue-600" />
              Yapay Zeka Çevirisi
            </DialogTitle>
            <DialogDescription>
              İçeriğiniz 7 farklı dile profesyonelce çevriliyor. Lütfen bekleyin.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div className="space-y-2">
              <div className="flex justify-between text-sm font-medium">
                <span>{currentStep}</span>
                <span>%{progress}</span>
              </div>
              <Progress value={progress} className="h-2 w-full" />
            </div>
            {progress === 100 && (
              <div className="flex items-center justify-center gap-2 text-green-600 animate-in fade-in zoom-in duration-300">
                <IconCircleCheck className="h-5 w-5" />
                <span className="font-medium text-sm">Başarıyla tamamlandı ve kaydedildi!</span>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <MediaPicker
        open={isPickerOpen}
        onOpenChange={setIsPickerOpen}
        onSelect={(url) => {
          if (pickerIndex !== null) {
            form.setValue(`members.${pickerIndex}.imageUrl`, url, { shouldDirty: true })
          }
          setIsPickerOpen(false)
        }}
        title="Personel Fotoğrafı Seç"
      />
    </>
  )
}
